import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogProps,
  FormLabel,
  InputGroup,
  InputMessage,
  InputText,
  Stack,
  Text,
} from "@aksara-ui/react";
import { Field, FieldProps, Form, Formik, FormikValues } from "formik";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { storeQuestMission, updateQuestMission } from "redux/actions/quest";
import { InputSelect } from "components/Form/InputSelect";
import ImageDropzone from "components/ImageDropzone";
import styled from "styled-components";
import * as yup from "yup";

const FormWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

interface Props {
  missionId: string;
}

const initialValues = {
  master_unit_id: "",
  name: "",
  slug: "",
  description: "",
  reward_points: 0,
  reward_badge_name: "",
  badge_name: "",
  tier_name: "",
  tier_image_active: "",
  tier_image_inactive: "",
  is_active: true,
};

const MissionFormModal: React.FC<Props & DialogProps> = ({
  isOpen,
  onClose,
  missionId,
}) => {
  const isAddMode = !missionId;
  const dispatch = useDispatch();
  const formikRef = useRef<FormikValues>() as any;

  const { questMissions, questUnits, isFormLoading } = useSelector(
    (state: any) => state.questReducer
  );
  const { appUrl } = useSelector((state: any) => state.globalReducer);

  const [filesBadgeActive, setFilesBadgeActive] = useState<any[]>([]);
  const [filesBadgeInactive, setFilesBadgeInactive] = useState<any[]>([]);

  const unitOptions = (Array.isArray(questUnits) ? questUnits : []).map((u: any) => ({
    label: u.name,
    value: String(u.id),
  }));

  const handleCloseModal = () => {
    setFilesBadgeActive([]);
    setFilesBadgeInactive([]);
    onClose && onClose();
  };

  const handleSubmit = (values: any) => {
    const badgeActiveFile =
      typeof filesBadgeActive[0] === "object"
        ? filesBadgeActive[0]
        : typeof filesBadgeActive[0] === "string"
          ? filesBadgeActive[0]
          : null;

    const badgeInactiveFile =
      typeof filesBadgeInactive[0] === "object"
        ? filesBadgeInactive[0]
        : typeof filesBadgeInactive[0] === "string"
          ? filesBadgeInactive[0]
          : null;

    const payload: any = {
      ...values,
      master_unit_id: values.master_unit_id
        ? Number(values.master_unit_id)
        : undefined,
      reward_points: Number(values.reward_points),
      is_active: values.is_active ? 1 : 0,
    };

    if (badgeActiveFile) {
      payload.tier_image_active = badgeActiveFile;
    }
    if (badgeInactiveFile) {
      payload.tier_image_inactive = badgeInactiveFile;
    }

    isAddMode
      ? dispatch(storeQuestMission(payload, handleCloseModal) as any)
      : dispatch(updateQuestMission(missionId, payload, handleCloseModal) as any);
  };

  useEffect(() => {
    if (!isAddMode && isOpen) {
      const detail = questMissions.find(
        (item: any) => String(item.id) === String(missionId)
      );
      if (detail && formikRef.current) {
        formikRef.current.setValues({
          master_unit_id: String(detail.master_unit_id ?? ""),
          name: detail.name ?? "",
          slug: detail.slug ?? "",
          description: detail.description ?? "",
          reward_points: detail.reward_points ?? 0,
          reward_badge_name: detail.reward_badge_name ?? "",
          badge_name: detail.badge_name ?? "",
          tier_name: detail.tier_name ?? "",
          tier_image_active: detail.tier_image_active ?? "",
          tier_image_inactive: detail.tier_image_inactive ?? "",
          is_active: detail.is_active ?? true,
        });

        setFilesBadgeActive(
          detail.tier_image_active
            ? [
              detail.tier_image_active.startsWith("http")
                ? detail.tier_image_active
                : `${appUrl}/storage/${detail.tier_image_active}`,
            ]
            : []
        );
        setFilesBadgeInactive(
          detail.tier_image_inactive
            ? [
              detail.tier_image_inactive.startsWith("http")
                ? detail.tier_image_inactive
                : `${appUrl}/storage/${detail.tier_image_inactive}`,
            ]
            : []
        );
      }
    } else if (isAddMode) {
      setFilesBadgeActive([]);
      setFilesBadgeInactive([]);
      if (formikRef.current) {
        formikRef.current.resetForm();
      }
    }
  }, [isAddMode, questMissions, missionId, isOpen, appUrl]);

  const validationSchema = yup.object({
    name: yup.string().required("Nama quest wajib diisi"),
    slug: yup.string().required("Slug wajib diisi"),
    master_unit_id: yup.string().required("Unit wajib dipilih"),
    reward_points: yup.number().min(0).required("Reward points wajib diisi"),
  });

  return (
    <Dialog
      disableOverlayClick
      isOpen={isOpen}
      onClose={() => handleCloseModal()}
      overflowY={"auto"}
    >
      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue }) => (
          <Form>
            <DialogHeader>
              <Text fontWeight={700}>
                {isAddMode ? "Tambah Quest" : "Edit Quest"}
              </Text>
            </DialogHeader>
            <DialogContent>
              <FormWrapper>
                {/* Unit */}
                <Stack spacing="xs">
                  <FormLabel>Unit</FormLabel>
                  <InputSelect
                    label=""
                    placeholder="Pilih Unit"
                    items={unitOptions}
                    selectedItem={unitOptions.find(
                      (o: any) => o.value === values.master_unit_id
                    ) ?? null}
                    itemToString={(item: any) => item?.label ?? ""}
                    handleSelectedItemChange={({ selectedItem }: any) =>
                      setFieldValue("master_unit_id", selectedItem?.value ?? "")
                    }
                  />
                </Stack>

                {/* Name */}
                <Field name="name">
                  {({ field, meta }: FieldProps<string>) => (
                    <Stack spacing="xs">
                      <FormLabel htmlFor="name">Nama Quest</FormLabel>
                      <InputGroup inputSize={40}>
                        <InputText
                          type="text"
                          size={40}
                          errors={!!meta.error && meta.touched}
                          {...field}
                        />
                      </InputGroup>
                      {!!meta.error && meta.touched && (
                        <InputMessage variant="error">{meta.error}</InputMessage>
                      )}
                    </Stack>
                  )}
                </Field>

                {/* Slug */}
                <Field name="slug">
                  {({ field, meta }: FieldProps<string>) => (
                    <Stack spacing="xs">
                      <FormLabel htmlFor="slug">Slug</FormLabel>
                      <InputGroup inputSize={40}>
                        <InputText
                          type="text"
                          size={40}
                          errors={!!meta.error && meta.touched}
                          placeholder="contoh: dufan-thrill"
                          {...field}
                        />
                      </InputGroup>
                      {!!meta.error && meta.touched && (
                        <InputMessage variant="error">{meta.error}</InputMessage>
                      )}
                    </Stack>
                  )}
                </Field>

                {/* Description */}
                <Field name="description">
                  {({ field }: FieldProps<string>) => (
                    <Stack spacing="xs">
                      <FormLabel htmlFor="description">Deskripsi</FormLabel>
                      <InputGroup inputSize={40}>
                        <InputText type="text" size={40} {...field} />
                      </InputGroup>
                    </Stack>
                  )}
                </Field>

                {/* Reward Points */}
                <Field name="reward_points">
                  {({ field, meta }: FieldProps<number>) => (
                    <Stack spacing="xs">
                      <FormLabel htmlFor="reward_points">Reward Points</FormLabel>
                      <InputGroup inputSize={40}>
                        <InputText
                          type="number"
                          size={40}
                          errors={!!meta.error && meta.touched}
                          {...field}
                        />
                      </InputGroup>
                      {!!meta.error && meta.touched && (
                        <InputMessage variant="error">{meta.error}</InputMessage>
                      )}
                    </Stack>
                  )}
                </Field>

                {/* Badge Name */}
                {/* <Field name="badge_name">
                  {({ field }: FieldProps<string>) => (
                    <Stack spacing="xs">
                      <FormLabel htmlFor="badge_name">Nama Badge</FormLabel>
                      <InputGroup inputSize={40}>
                        <InputText type="text" size={40} placeholder="contoh: Thrill Seeker" {...field} />
                      </InputGroup>
                    </Stack>
                  )}
                </Field> */}

                {/* Tier Name */}
                {/* <Field name="tier_name">
                  {({ field }: FieldProps<string>) => (
                    <Stack spacing="xs">
                      <FormLabel htmlFor="tier_name">Tier Badge</FormLabel>
                      <InputGroup inputSize={40}>
                        <InputText type="text" size={40} placeholder="contoh: Gold" {...field} />
                      </InputGroup>
                    </Stack>
                  )}
                </Field> */}

                {/* Badge Image Active */}
                <Stack spacing="xs">
                  <FormLabel>Thumbnail Image (Active)</FormLabel>
                  <ImageDropzone
                    files={filesBadgeActive}
                    setFiles={setFilesBadgeActive}
                  />
                </Stack>

                {/* Badge Image Inactive */}
                <Stack spacing="xs">
                  <FormLabel>Thumbnail Image (Inactive)</FormLabel>
                  <ImageDropzone
                    files={filesBadgeInactive}
                    setFiles={setFilesBadgeInactive}
                  />
                </Stack>
              </FormWrapper>
            </DialogContent>
            <DialogFooter>
              <Stack
                display="flex"
                flexDirection="row"
                spacing="xs"
                flex={1}
                style={{ gap: 15 }}
              >
                <Button
                  variant="outline"
                  block
                  type="button"
                  onClick={() => handleCloseModal()}
                >
                  Batal
                </Button>
                <Button
                  variant="primary"
                  block
                  type="submit"
                  isLoading={isFormLoading}
                >
                  {isAddMode ? "Simpan" : "Perbarui"}
                </Button>
              </Stack>
            </DialogFooter>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default MissionFormModal;
